import { useContext, useEffect, useState } from 'react';
import { useLoaderData, useNavigate, useParams } from 'react-router-dom';
import { useImmerReducer } from 'use-immer';
import { SmartContext } from '../../library/Core/SmartContext';
import smartReducer from '../../library/Core/SmartReducer';
import { ControlOnChangeArguments, DispatchEvent, State } from '../../library/Core/SmartTypes';
import { GlobalContext } from '../../library/Core/GlobalContext';
import { handlePreview, handleReset, handleSave } from './WidgetConfigurationActions';
import SessionContext from '../../library/Core/SessionContext';
import CurrentLocationCountry from './forms/CurrentLocationCountry';
import TopIndustries from './forms/TopIndustries';
import { triggerGridDataOnChange } from '../../library/Core/SmartFunctions';
import AlumniOccupation from './forms/AlumniOccupation';
import AppealVideo from './forms/AppealVideo';
import CoverBanner from './forms/CoverBanner';
import EnquiryForm from './forms/EnquiryForm';
import AdmissionReferralForm from './forms/AdmissionReferralForm';
import useManagementDashboard from '../management/dashboard/useManagementDashboard';
import { getinstituteList } from '../../library/Service/domainService';
import FormListService from './FormListService';
import { axiosClient } from '../../library/Service/axiosClient';
import { fetchProgramOptions, toProgramDomainElements } from './WidgetProgramService';

const Configuration = () => {
    const navigate = useNavigate();
    const { globalState } = useContext(GlobalContext);
    const { sessionState } = useContext(SessionContext);
    const { allForms } = FormListService();
    const { getWidgetCampuses } = useManagementDashboard();
    const campuses = getWidgetCampuses();
    const [instituteList, setinstituteList] = useState(getinstituteList('INSTITUTE_GROUP_CODE', campuses));

    const { formConfig, data, internal, routeInfo } = useLoaderData() as State;
    const [state, dispatch] = useImmerReducer<State, DispatchEvent>(smartReducer, {
        flags: { isDataLoading: true, showFormErrors: 0 },
        formValidationErrors: {},
        internal: internal,
        data: data,
        actions: {
            RESET: handleReset,
            SAVE: handleSave,
            PREVIEW: handlePreview,
            REDIRECT: navigate,
            instituteList: onInstituteListChange,
            programId: onProgramChange,
        },
    } as any);

    let {id, code}: any = useParams();
    const isEditing = !!id;

    if ( typeof code == 'undefined') {
        code = '';
    }

    useEffect(() => {
        const fetchData = async () => {
            try {
                dispatch({
                    type: 'DATA_INIT',
                    payload: {
                        formConfig,
                        data,
                        domain: new Map([...globalState.domain]),
                        internal,
                        routeInfo,
                    },
                });
                dispatch({ type: 'RESET_VALIDATION_ERRORS' });
            } catch (error) {
                console.error('Error fetching program data:', error);
            }
            dispatch({
                type: 'SET_DOMAIN',
                payload: { key: 'INSTITUTE_GROUP_CODE', value: instituteList },
            });

            const userObjMount = JSON.parse(localStorage.getItem('user') || '{}');
            const isSuperAdminMount = userObjMount?.role === 'SUPER_ADMIN' || userObjMount?.userTypeCode === 'SUPER_ADMIN';
            const widgetGroupMount = isSuperAdminMount ? (localStorage.getItem('widgetGroup') || '') : '';
            const widgetInstituteMount = isSuperAdminMount ? (localStorage.getItem('widgetInstitute') || '') : '';
            const currentInstituteId = data?.widgetData?.instituteList || (!widgetGroupMount ? widgetInstituteMount : '');
            const mountGroupId = isSuperAdminMount ? widgetGroupMount : (userObjMount?.instituteGroupId || '');
            const mountProgramId = data?.widgetData?.programId ?? null;

            const [FromWthInstitute, programOptions] = await Promise.all([
                allForms(currentInstituteId || '', mountProgramId || null),
                fetchProgramOptions(currentInstituteId || null, mountGroupId || null),
            ]);

            console.log(FromWthInstitute, 'FORM_WITH_INSTITUTE')
            dispatch({
                type: "SET_DOMAIN",
                payload: { key: "FORM_WITH_INSTITUTE", value: FromWthInstitute || [] },
            });
            dispatch({
                type: 'SET_DOMAIN',
                payload: { key: 'PROGRAM_LIST', value: toProgramDomainElements(programOptions) },
            });
        };
    
        fetchData();
    }, []);

    // Re-populate INSTITUTE_GROUP_CODE domain after DATA_INIT runs and whenever the
    // institute domain changes. Prevents an empty dropdown when initial useState
    // snapshot of campuses was empty.
    // For SUPER_ADMIN: if a specific institute was picked on the listing-page filter
    // (localStorage.widgetInstitute), narrow the dropdown to that single institute
    // instead of showing all institutes in the group.
    useEffect(() => {
        if (state.flags.isDataLoading) return;

        const isSuperAdminUser = sessionState?.userTypeCode === 'SUPER_ADMIN';
        const widgetGroupFromList = isSuperAdminUser ? (localStorage.getItem('widgetGroup') || '') : '';
        const widgetInstituteFromList = isSuperAdminUser ? (localStorage.getItem('widgetInstitute') || '') : '';

        // SUPER_ADMIN: if user picked ONLY an institute (no group) on the listing
        // filter, scope the dropdown to that single institute (sourced directly
        // from INSTITUTE_DOMAIN to bypass is_published). Otherwise show the full
        // group list (group + optional institute pre-selection case).
        const freshCampuses = getWidgetCampuses();
        let scopedCampuses = freshCampuses;
        if (widgetInstituteFromList && !widgetGroupFromList) {
            const inst = (globalState.domain.get('INSTITUTE_DOMAIN') as any[])
                ?.find((item: any) => String(item.code) === String(widgetInstituteFromList));
            scopedCampuses = inst ? [{ label: inst.value, value: inst.code }] : freshCampuses;
        }
        const freshList = getinstituteList('INSTITUTE_GROUP_CODE', scopedCampuses);
        dispatch({
            type: 'SET_DOMAIN',
            payload: { key: 'INSTITUTE_GROUP_CODE', value: freshList },
        });

        // SUPER_ADMIN with group + institute filter: pre-populate the chosen institute
        // in the form (don't hide the dropdown — user may want to switch institutes).
        if (isSuperAdminUser && widgetInstituteFromList && widgetGroupFromList && state.data?.widgetData) {
            if (!state.data.widgetData.instituteList) {
                dispatch({
                    type: 'CONTROL_VALUE_CHANGE',
                    payload: { dataKey: 'widgetData.instituteList', value: widgetInstituteFromList },
                });
            }
        }

        // Auto-select + hide only when the dropdown has exactly 1 option
        // (INSTITUTE_ADMIN with a single institute, or a group with 1 institute).
        if (freshList?.length === 1 && state.data?.widgetData) {
            const onlyInstituteCode = freshList[0].code;
            if (state.data.widgetData.instituteList !== onlyInstituteCode) {
                dispatch({
                    type: 'CONTROL_VALUE_CHANGE',
                    payload: { dataKey: 'widgetData.instituteList', value: onlyInstituteCode },
                });
            }
            const currentConfig = state.formConfig as any;
            let needsPatch = false;
            const patchedConfig = currentConfig ? JSON.parse(JSON.stringify(currentConfig)) : null;
            patchedConfig?.sectionRepository?.forEach((section: any) => {
                section.controlGroup?.forEach((control: any) => {
                    if (control.id === 'instituteList' && control.hideExpression !== '1') {
                        control.hideExpression = '1';
                        needsPatch = true;
                    }
                });
            });
            if (needsPatch && patchedConfig) {
                dispatch({ type: 'FORM_CONFIG', payload: patchedConfig });
            }
        }
    }, [state.flags.isDataLoading, globalState.domain, sessionState?.institutes, sessionState?.userTypeCode, state.formConfig]);

    useEffect(() => {
        if (isEditing) {
            let btnList = "RESET,SAVE,PREVIEW";
            const noResetWidgets = ['appeal_video', 'cover_banner', 'enquiry_form', 'admission_referral_form'];
            if (noResetWidgets.includes(code) ) {
                btnList = "SAVE,PREVIEW";
            }
            dispatch({ type: 'FORM_CONFIG', payload: { ...formConfig, "buttons": btnList } });
            dispatch({
                type: 'SET_DOMAIN',
                payload: { key: 'INSTITUTE_GROUP_CODE', value: instituteList },
            });
        }
    }, [isEditing]);

    useEffect(() => {
        setTimeout(() => {
            triggerGridDataOnChange(state?.data?.metricData, "metricData", "metricEntity");
        }, 500);
    }, [state?.data?.metricData])

    async function onInstituteListChange(props: ControlOnChangeArguments) {
        if (!code) return;

        const newInstituteId = props?.value;
        const userObj = JSON.parse(localStorage.getItem('user') || '{}');
        const isSuperAdminUser = userObj?.role === 'SUPER_ADMIN' || userObj?.userTypeCode === 'SUPER_ADMIN';
        const groupId = isSuperAdminUser
            ? (localStorage.getItem('widgetGroup') || '')
            : (userObj?.instituteGroupId || '');
        const queryParts: string[] = [];
        if (newInstituteId) queryParts.push(`instituteId=${newInstituteId}`);
        if (groupId) queryParts.push(`instituteGroupId=${groupId}`);
        const instituteQry = queryParts.length ? `?${queryParts.join('&')}` : '';

        // Refresh the form list (FORM_WITH_INSTITUTE) so the formlist dropdown
        // reflects forms relevant to the newly selected institute.
        try {
            const refreshedForms = await allForms(newInstituteId || '', null);
            dispatch({
                type: 'SET_DOMAIN',
                payload: { key: 'FORM_WITH_INSTITUTE', value: refreshedForms || [] },
            });
            props.dispatch({
                type: 'CONTROL_VALUE_CHANGE',
                payload: { dataKey: 'widgetData.formlist', value: null },
            });
        } catch (err) {
            console.error('Error refreshing FORM_WITH_INSTITUTE:', err);
        }

        const programOptions = await fetchProgramOptions(newInstituteId || null, groupId || null);
        dispatch({
            type: 'SET_DOMAIN',
            payload: { key: 'PROGRAM_LIST', value: toProgramDomainElements(programOptions) },
        });
        props.dispatch({
            type: 'CONTROL_VALUE_CHANGE',
            payload: { dataKey: 'widgetData.programId', value: null },
        });

        const endpointMap: any = {
            current_location_country: 'get-current-location-country-counts',
            top_industries: 'get-top-industry-counts',
            alumni_occupation: 'get-occupation-counts',
        };
        const endpoint = endpointMap[code];
        if (!endpoint) return;

        const response = await axiosClient().get(`${process.env.REACT_APP_WIDGET_API_SERVER}/widgetInfo/${endpoint}${instituteQry}`);
        const newCounts = response.data;

        dispatch({
            type: 'SET_INTERNAL_STATE',
            payload: { key: code, value: newCounts },
        });

        const currentMetricData = props.state.data?.metricData;
        if (Array.isArray(currentMetricData)) {
            let updated = currentMetricData.map((item: any) => ({ ...item }));

            if (code === 'alumni_occupation' && newCounts && typeof newCounts === 'object' && !Array.isArray(newCounts)) {
                updated = Object.keys(newCounts || {}).slice(0, 4).map((key: string, index: number) => ({
                    ...(currentMetricData[index] || {}),
                    id: index,
                    metricEntity: key,
                }));
            } else if (Array.isArray(newCounts)) {
                const fetchedData = newCounts.filter((rec: any) => String(rec?.metricEntityCode) !== '0');
                updated = fetchedData.slice(0, 4).map((rec: any, index: number) => ({
                    ...(currentMetricData[index] || {}),
                    id: index,
                    metricEntity: rec?.metricEntityCode,
                }));
            }

            props.dispatch({
                type: 'CONTROL_VALUE_CHANGE',
                payload: { dataKey: 'metricData', value: updated },
            });

            setTimeout(() => {
                triggerGridDataOnChange(updated, 'metricData', 'metricEntity');
            }, 500);
        }
    }

    async function onProgramChange(props: ControlOnChangeArguments) {
        if (!code) return;

        const programId = props?.value;
        const userObj = JSON.parse(localStorage.getItem('user') || '{}');
        const isSuperAdminUser = userObj?.role === 'SUPER_ADMIN' || userObj?.userTypeCode === 'SUPER_ADMIN';
        const newInstituteId = props?.state?.data?.widgetData?.instituteList;
        const groupId = isSuperAdminUser
            ? (localStorage.getItem('widgetGroup') || '')
            : (userObj?.instituteGroupId || '');
        const queryParts: string[] = [];
        if (newInstituteId) queryParts.push(`instituteId=${newInstituteId}`);
        if (groupId) queryParts.push(`instituteGroupId=${groupId}`);
        if (programId) queryParts.push(`programId=${programId}`);
        const instituteQry = queryParts.length ? `?${queryParts.join('&')}` : '';

        // Refresh enquiry form list filtered by the selected program.
        try {
            const refreshedForms = await allForms(newInstituteId || '', programId || null);
            dispatch({
                type: 'SET_DOMAIN',
                payload: { key: 'FORM_WITH_INSTITUTE', value: refreshedForms || [] },
            });
            props.dispatch({
                type: 'CONTROL_VALUE_CHANGE',
                payload: { dataKey: 'widgetData.formlist', value: null },
            });
        } catch (err) {
            console.error('Error refreshing FORM_WITH_INSTITUTE:', err);
        }

        const endpointMap: any = {
            current_location_country: 'get-current-location-country-counts',
            top_industries: 'get-top-industry-counts',
            alumni_occupation: 'get-occupation-counts',
        };
        const endpoint = endpointMap[code];
        if (!endpoint) return;

        try {
            const response = await axiosClient().get(`${process.env.REACT_APP_WIDGET_API_SERVER}/widgetInfo/${endpoint}${instituteQry}`);
            const newCounts = response.data;

            dispatch({
                type: 'SET_INTERNAL_STATE',
                payload: { key: code, value: newCounts },
            });

            const currentMetricData = props.state.data?.metricData;
            if (Array.isArray(currentMetricData)) {
                let updated = currentMetricData.map((item: any) => ({ ...item }));

                if (code === 'alumni_occupation' && newCounts && typeof newCounts === 'object' && !Array.isArray(newCounts)) {
                    updated = Object.keys(newCounts || {}).slice(0, 4).map((key: string, index: number) => ({
                        ...(currentMetricData[index] || {}),
                        id: index,
                        metricEntity: key,
                    }));
                } else if (Array.isArray(newCounts)) {
                    const fetchedData = newCounts.filter((rec: any) => String(rec?.metricEntityCode) !== '0');
                    updated = fetchedData.slice(0, 4).map((rec: any, index: number) => ({
                        ...(currentMetricData[index] || {}),
                        id: index,
                        metricEntity: rec?.metricEntityCode,
                    }));
                }

                props.dispatch({
                    type: 'CONTROL_VALUE_CHANGE',
                    payload: { dataKey: 'metricData', value: updated },
                });

                setTimeout(() => {
                    triggerGridDataOnChange(updated, 'metricData', 'metricEntity');
                }, 500);
            }
        } catch (err) {
            console.error('Error in onProgramChange:', err);
        }
    }

    return (
        <SmartContext.Provider value={{ state, dispatch }}>
            {code == 'current_location_country' && !state.flags.isDataLoading && <CurrentLocationCountry />}

            {code == 'top_industries' && !state.flags.isDataLoading && <TopIndustries />}

            {code == 'alumni_occupation' && !state.flags.isDataLoading && <AlumniOccupation />}

            {code == 'appeal_video' && !state.flags.isDataLoading && <AppealVideo />}

            {code == 'cover_banner' && !state.flags.isDataLoading && <CoverBanner />}

            {code == 'enquiry_form' && !state.flags.isDataLoading && <EnquiryForm />}

            {code == 'admission_referral_form' && !state.flags.isDataLoading && <AdmissionReferralForm />}

        </SmartContext.Provider>
    );
};

export default Configuration;
